import revalidator from 'revalidator';

class ValidatorService {

    constructor ({ data, validation }) {
        this.data = data;
        this.validation= validation;
    }

    validate () {
        return revalidator.validate(this.data, { properties: this.validation });
    }
}

class AjaxAction {

    constructor (endpointService, endpointDefinition) {
        this.endpointService = endpointService;
        this.endpointDefinition = endpointDefinition;
    }

    /**
     * Executes an action to recover a list of centers.
     * @param {Object} options Performs parameter validation on them if the endpoint definition contains a params
     *                         object with a revalidator-compatible specification.
     * @returns {Promise} A Promise that resolves when the process of recovering the data is finished.
     */
    invoke (options) {
        // single out the URL path parameters, to be added to the AJAX path
        let endpointPath = this.endpointDefinition.path;
        const endpointParams = this.endpointDefinition.params;
        let endpointUrlParams = this.endpointDefinition.urlParams;

        let requestParams;
        if (this.isEmptyObject(endpointUrlParams)) {
            requestParams = options;
        }
        else {
            requestParams = {};
            for (let key of Object.keys(endpointUrlParams)) {
                let paramName = endpointUrlParams[key];
                let paramValue = options[paramName];
                if (!paramValue) {
                    throw new Error(`AjaxAction#invoke: undefined param "${paramName}"`);
                }
                endpointPath = endpointPath.replace(key, paramValue);
            }

            // single out the request parameters, to be sent with the AJAX request
            const urlParamNames = Object.values(endpointUrlParams);
            Object.keys(options).forEach(key => {
                if (!urlParamNames.includes(key)) {
                    requestParams[key] = options[key];
                }
            });
        }

        if (!this.isEmptyObject(endpointParams)) {
            const validator = new ValidatorService({ data: requestParams, validation: endpointParams });
            const validation = validator.validate();
            if (!validation.valid) {
                return Promise.reject(validation);
            }
        }

        if (this.endpointDefinition.activeRequestConfig) {
            this.endpointService.httpApi.activeRequestConfig = this.endpointDefinition.activeRequestConfig;
        }

        const path = `${this.endpointService.apiPrefix}${endpointPath}`;
        return this.endpointService.run(this.endpointDefinition.method, path, requestParams);
    }

    isEmptyObject (object) {
        object = object || {};
        return Object.keys(object).length === 0;
    }

}

class EndpointService {

    constructor (httpApi) {
        if (!httpApi) throw new Error('Missing param: httpApi');
        this.httpApi = httpApi;
    }

    run (method, path, params) {
        switch (method) {
            case 'GET':
                return this.httpApi.get(path, params);
            case 'POST':
                return this.httpApi.post(path, params);
            case 'PUT':
                return this.httpApi.put(path, params);
            case 'DELETE':
                return this.httpApi.del(path, params);
            case 'PATCH':
                return this.httpApi.patch(path, params);
            default:
                throw new Error('EndpointService: bad method');
        }

    }

    get apiPrefix () {
        return this.httpApi.apiPrefix;
    }

}

class AutoParser {

    parse (response, responseType) {
        if (typeof response === 'string' && responseType === 'json') {
            // PATCH for IE11 omitting request.responseType
            try {
                return JSON.parse(response)
            }
            catch (e) {
                return {};
            }
        }

        return response;
    }
}

class HttpInterface {

    constructor(options) {
        this.SessionStoreCookieFactory = options.sessionStoreCookieFactory;
        this.apiPrefix = options.apiPrefix;
        this.httpParams = options.httpParams;
        this.responseType = options.type || 'application/json';
    }

    get () {
        throw new Error('HttpApiInterface: Do not directly use this class');
    }

    post () {
        throw new Error('HttpApiInterface: Do not directly use this class');
    }

    put () {
        throw new Error('HttpApiInterface: Do not directly use this class');
    }

    del () {
        throw new Error('HttpApiInterface: Do not directly use this class');
    }

    patch () {
        throw new Error('HttpApiInterface: Do not directly use this class');
    }

    _getAuthorization (path) {
        if (this.httpParams && this.httpParams.authorization) {
            return this.httpParams.authorization;
        }
        return this._getSessionStoreCookie(path).get('access');
    }

    _getSessionStoreCookie (path) {
        if (!this.sessionStoreCookie) {
            this.sessionStoreCookie = new this.SessionStoreCookieFactory({
                apiTimeout: 10,
                apiEndpoint: path
            }, this.apiPrefix );
        }
        return this.sessionStoreCookie;
    }

}

const RES_TYPES = {
    'application/json': 'json',
    'application/pdf': 'blob',
    'text/plain': 'text',
};

class XhrHttpIO extends HttpInterface {

    get (path, params, responseType) {
        const request = new XMLHttpRequest();

        let endpoint = this.getEndpoint(path);
        const paramsQs = this.objToQuerystring(params);
        if (paramsQs) {
            endpoint = endpoint + paramsQs;
        }
        request.open('GET', endpoint);
        const authorization = this._getAuthorization(path);

        this.setupAuthorization(request, authorization);
        this.setupResponseType(request, responseType || this.responseType);
        this.setupAjaxCacheBuster(request);
        this.setupExtraHeaders(request);
        this.setupCustomHeaders(request);
        return this.promisify(request);
    }

    post (path, params, responseType) {
        return this.doRequestWithBody('POST', path, params, responseType);
    }

    put (path, params, responseType) {
        return this.doRequestWithBody('PUT', path, params, responseType);
    }

    del (path, params, responseType) {
        return this.doRequestWithBody('DELETE', path, params, responseType);
    }

    patch (path, params, responseType) {
        return this.doRequestWithBody('PATCH', path, params, responseType);
    }

    doRequestWithBody(method, path, params, responseType) {
        const request = new XMLHttpRequest();
        const endpoint = this.getEndpoint(path);
        request.open(method, endpoint);

        const authorization = this._getAuthorization(path);
        this.setupAuthorization(request, authorization);
        this.setupResponseType(request, responseType);
        this.setupExtraHeaders(request);
        this.setupCustomHeaders(request);
        return this.promisify(request, params);
    }

    getEndpoint (path) {
        if (path.includes('://')) {
            return path;
        }

        if (!path.startsWith('/')) {
            path = `/${path}`;
        }
        return `${this.apiPrefix}${path}`;
    }

    promisify (request, params) {
        const self = this;
        const p = new Promise(function (resolve, reject) {
            request.onload = self.onLoadHandler.bind(self, request, resolve, reject);
            request.onerror = self.onErrorHandler.bind(self, request, reject);
            request.send(JSON.stringify(params));
        });
        p.headers = request.requestHeaders;
        return p;
    }

    onLoadHandler (request, resolve, reject) {
        if (request.status >= 200 && request.status < 300) {
            const autoParser = new AutoParser();
            request.body = autoParser.parse(request.response, request._msResponseType);
            resolve(request);
        }
        else {
            this.onErrorHandler(request, reject);
        }
    }

    onErrorHandler (request, reject) {
        reject({
            status: request.status,
            statusText: request.statusText,
            error: request.error,
            request
        });
    }

    objToQuerystring (obj) {
        if (typeof obj === 'undefined') {
            return;
        }

        const objectKeys = Object.keys(obj);
        if (objectKeys.length === 0) {
            return;
        }

        const querystring = objectKeys.map(k => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
            .join('&');
        return '?' + querystring;
    }

    setupAjaxCacheBuster (request) {
        request.setRequestHeader('Expires', '0');
        request.setRequestHeader('Pragma', 'no-cache');
        request.setRequestHeader('Cache-Control', 'no-cache,no-store,must-revalidate');
    }

    setupAuthorization (request, authorization) {
        request.withCredentials = true;
        if (!!authorization) {
            request.setRequestHeader('Authorization', authorization);
        }
    }

    setupResponseType (request, responseType) {
        const type = responseType || 'application/json';
        request.setRequestHeader('accept', type);
        request.setRequestHeader('content-type', 'application/json');
        // PATCH for IE11 omitting request.responseType
        request._msResponseType = RES_TYPES[responseType] || 'json';
        request.responseType = request._msResponseType;
    }

    setupExtraHeaders (request) {
        // Override in a subclass
    }

    setupCustomHeaders (request) {
        if (!this.httpParams || !this.httpParams.headers) {
            return;
        }

        for (let [name, value] of Object.entries(this.httpParams.headers)) {
            this.setHeader({ request, name, value });
        }
    }

    setHeader ({ request, name, value }) {
        request.setRequestHeader(name, value);
    }


}

class ActiveRequester extends XhrHttpIO {

    constructor(options) {
        super({
            sessionStoreCookieFactory: options.sessionStoreCookieFactory,
            apiPrefix: options.apiPrefix
        });

        const { httpParams, type } = options;

        this.attempts = 0;
        this.maxAttempts = 1;
        this.firstThrottle = 1000;
        this.throttle = 3000;
        this.waitCodes = [202];
        this.responseType = type || 'application/json';

        if (typeof httpParams === 'object') {
            const { fnShouldAbort } = httpParams;
            if (typeof fnShouldAbort === 'function') {
                this.shouldAbort = fnShouldAbort;
            }
        }
    }

    get (path, params) {
        this.path = path;
        this.params = params || {};
        return this.timedAjaxCall();
    }

    timedAjaxCall () {
        if (this.attempts > this.maxAttempts) {
            return Promise.reject(`Number of attempts exceeded: ${this.attempts}`);
        }

        this.attempts += 1;

        return this.delay()
        .then(this.ajaxCall.bind(this))
        .then(this.ajaxHandler.bind(this));
    }

    delay () {
        return new Promise(resolve => {
            const msToThrottle = this.attempts === 1 ? 0
                                :this.attempts === 2 ? this.firstThrottle
                                :this.throttle;
            setTimeout(resolve, msToThrottle);
        });
    }

    ajaxCall () {
        return super.get(this.path, this.requestParams, this.responseType);
    }

    ajaxHandler (res) {
        if (this.shouldAbort()) {
            return Promise.reject('Aborted')
        }
        if (this.isWaitCode(res.status)) {
            return this.timedAjaxCall();
        }
        return res;
    }

    isWaitCode (code) {
        return this.waitCodes.includes(code);
    }

    shouldAbort () {
        return false;
    }

    set activeRequestConfig (options) {
        this.firstThrottle = options.start || this.firstThrottle;
        this.throttle = options.interval || this.throttle;
        this.maxAttempts = options.maxAttempts || this.maxAttempts;
        this.waitCodes = options.waitCodes || this.waitCodes;
    }

    get timestamp () {

        return new Date().getTime();
    }

    get requestParams () {
        const params = Object.assign({}, this.params);
        if (!params.c){
            params.c = this.timestamp;
        }
        return params;
    }

}

class AjaxReducer {

    constructor ({ reducerParams }) {
        this.reducerParams = reducerParams;
    }

    reduce (data) {
        return {
            data,
            reducerParams: this.reducerParams,
        };
    }

}

class AjaxHandler {

    constructor (options) {
        if (!options.action && !options.endpointDefinition) {
            throw new Error('endpointDefinition param is required if no action param is passed');
        }
        this.apiPrefix = options.apiPrefix;
        this.action = options.action || AjaxAction;
        this.reducer = options.reducer || AjaxReducer;
        this.reducerParams = options.reducerParams || [];
        this.errorHandler = options.errorHandler;
        this.sessionStoreCookie = options.sessionStoreCookie; // CV
        this.tokenCookie = options.tokenCookie;  // JRP
        this.endpointDefinition = options.endpointDefinition;
        this.endpointService = options.endpointService || EndpointService;

        // TODO remove
        const isActiveRequest = options.endpointDefinition && options.endpointDefinition.activeRequestConfig;

        this.httpIo = options.httpIo || (isActiveRequest? ActiveRequester : XhrHttpIO);
        // TODO use httpParams
        this.httpIoParams = options.httpIoParams || undefined;

        if (options.httpParams) {
            this.httpParams = options.httpParams;
        }
    }

    customAction (requestParams) {
        const httpIoInstance = this.getHttpIoInstance();
        const endpointService = new this.endpointService(httpIoInstance);

        const Action = this.action;
        const action = new Action(endpointService, this.endpointDefinition);

        const invoke = action.invoke(requestParams);
        invoke.catch(this.onActionInvokeError.bind(this));
        return invoke.then(this.reduceResult.bind(this));
    }

    getHttpIoInstance () {
        return new this.httpIo({
            sessionStoreCookieFactory: this.sessionStoreCookie,
            tokenCookie: this.tokenCookie,
            apiPrefix: this.apiPrefix,
            httpParams: this.httpParams,
            ...this.httpIoParams
        });
    }

    reduceResult (actionResult) {
        const Reducer = this.reducer;
        const reducer = new Reducer({
            reducerParams: this.reducerParams
        });
        return reducer.reduce(actionResult, this.reducerParams);
    };

    onActionInvokeError (err) {
        this.errorHandler({
            className: `AjaxHandler-${this.action.name}`,
            funcName: 'onActionInvokeError',
            err
        });
        return err;
    }

}

const LoggerService = {
    log: console.log.bind(undefined)
};

class AjaxMiddleware {

    constructor (options) {
        if (options && options.logger) {
            this.logger = options.logger;
        }
        else {
            this.logger = LoggerService.log;
        }
    }

    invoke (options) {
        try {
            return this._invoke(options);
        }
        catch (e) {
            // If there's an implementation error, go forward
            this.logger({
                className: 'AjaxMiddleWare',
                funcName: 'invoke',
                err: e
            });
            return true;
        }
    }

    _invoke() {
        throw new Error('Implement in a subclass. It will be automatically called.');
    }
}

class UrlService {

    static getQueryStringItemValue (url, item) {
        let link = document.createElement("a");
        link.href = url;
        let queryString = link.search;

        if (queryString.length <= 1) {
            return null;
        }
        queryString = queryString.slice(1);

        let params = queryString.split("&");
        let items = {};
        let parts = [];
        for (let i = 0; i < params.length; i++) {
            parts = params[i].split("=");
            items[parts[0]] = parts[1];
        }

        if (items[item]) {
            return items[item];
        }

        return null;
    };

    static navigateTo(url) {
        window.location.assign(url);
    }
}

class LogOutOnCodesMiddleware extends AjaxMiddleware {

    constructor (options) {
        super({ logger: options });
        const codes = options? options.codes : undefined;
        this._validateCodeRanges(codes);
        this.codes = codes;
        this.signOutUrl = options? options.signOutUrl : '/signout';
    }

    invoke (httpCode) {
        httpCode = Number(httpCode);
        if (!this.codes ||
            isNaN(httpCode) ||
            !this.codes.includes(httpCode)
        ) {
            return true;
        }

        UrlService.navigateTo(this.signOutUrl);
        return false;
    }

    _validateCodeRanges (codes) {
        if (!codes) {
            return;
        }

        if (!Array.isArray(codes)) {
            throw new Error('Message for developer: must be a Number array');
        }
        codes.forEach(code => {
            if (isNaN(code)) {
                throw new Error('Message for developer: each Array element must be a number');
            }
        });
    }

}

class LogOutOnCodeRangesMiddleware extends AjaxMiddleware{

    constructor (options) {
        super({ logger: options });
        const codeRanges = options? options.codeRanges : undefined;
        this._validateCodeRanges(codeRanges);
        this.codeRanges = codeRanges;
        this.signOutUrl = options? options.signOutUrl : '/signout';
    }

    _invoke (httpCode) {
        const expandedCodes = this._expandCodes(this.codeRanges);
        const logOutOnCodes = new LogOutOnCodesMiddleware({
            logger: this.logger,
            codes: expandedCodes,
            signOutUrl: this.signOutUrl
        });
        return logOutOnCodes.invoke(httpCode);
    }

    _validateCodeRanges (codeRanges) {
        if (!codeRanges) {
            return;
        }

        if (!Array.isArray(codeRanges)) {
            throw new Error('Message for developer: must be a Number array');
        }
        codeRanges.forEach(range => {
            if (isNaN(range.min) || isNaN(range.max)) {
                throw new Error('min and max are required in each range');
            }
            if (typeof range.min !== 'number' || typeof range.max !== 'number') {
                throw new Error('Message for developer: must be a Number array');
            }
            if (range.min >= range.max) {
                throw new Error('max must be greater than min')
            }
        });
    }

    _expandCodes (codeRanges) {
        if(!codeRanges) return codeRanges;

        const codes = [];
        codeRanges.forEach(range => {
           for (let i = range.min; i <= range.max; i++) {
               codes.push(i);
           }
        });
        return codes;
    }

}

class QueryStringService {

    extractSubQueryStringObject(url, fields) {
        let values = {};
        let queryStringValues = undefined;

        if (!url || !fields || fields.length === 0) {
            return values;
        }

        let queryString = url.replace(/^\?|\/$/g, '');

        for(let item of queryString.split('&')) {
            let keyValues = item.split('=');
            queryStringValues = queryStringValues || {};
            queryStringValues[keyValues[0]] = keyValues[1] || undefined;
        }

        for(let field of fields) {
            values[field] = queryStringValues[field] || undefined;
        }

        return values;
    }

}

export { ActiveRequester, AjaxAction, AjaxHandler, AjaxMiddleware, AutoParser, EndpointService, HttpInterface, LogOutOnCodeRangesMiddleware, LogOutOnCodesMiddleware, LoggerService, QueryStringService, UrlService, ValidatorService, XhrHttpIO };
